import { motion } from "framer-motion";
import { Swords, Download, Star, Info, MapPin, Trophy } from "lucide-react";
import { API } from "@/lib/api";
import { ProvBadge } from "@/lib/provenance";

const fade = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } };

const CLASS_CHIP = {
  DIRECT: "text-red-700 bg-red-50 border-red-200",
  NEARBY: "text-sky-700 bg-sky-50 border-sky-200",
  PREMIUM: "text-purple-700 bg-purple-50 border-purple-200",
  VALUE: "text-emerald-700 bg-emerald-50 border-emerald-200",
  UNCATEGORIZED: "text-slate-600 bg-slate-100 border-slate-200",
};
const CLASS_LABEL = {
  DIRECT: "Direct competitor", NEARBY: "Nearby alternative", PREMIUM: "Premium alternative",
  VALUE: "Value alternative", UNCATEGORIZED: "Uncategorized",
};

const SOURCE_NOTE = {
  google_places_verified: "Verified via Google Places",
  self_reported: "Owner-entered (unverified)",
  unverified_ai: "AI-suggested competitors to research — not confirmed facts",
  none: "No competitor data available yet",
};

// Build the "your business" row + competitor rows from the report — never fabricates a value.
function buildRows(report, business) {
  const rep = report?.reputation || {};
  const meta = rep.metrics_meta || {};
  const metrics = rep.metrics || {};
  const repStatus = rep.verification_status || "NONE";
  const yourMetric = (key) => {
    const m = meta[key] || {};
    const value = m.value != null ? m.value : metrics[key];
    const status = m.status || (value != null ? repStatus : "NONE");
    return { value: value ?? null, status };
  };
  const your = {
    name: business?.name || "Your business",
    you: true,
    classification: null,
    rating: yourMetric("rating"),
    reviews: yourMetric("review_count"),
    location: [business?.city, business?.state].filter(Boolean).join(", ") || null,
    category: business?.category || null,
    status: repStatus,
  };

  const comp = report?.competitors || {};
  const rows = [];
  (comp.businesses || []).forEach((b) => {
    if (!b.name) return;
    const st = b.status || "VERIFIED";
    const hasRating = typeof b.rating === "number";
    const hasReviews = typeof b.review_count === "number";
    rows.push({
      name: b.name, you: false, classification: b.classification,
      rating: { value: hasRating ? b.rating : null, status: hasRating ? st : "UNABLE_TO_VERIFY" },
      reviews: { value: hasReviews ? b.review_count : null, status: hasReviews ? st : "UNABLE_TO_VERIFY" },
      location: b.address || null, category: b.category || null, status: st,
    });
  });
  (comp.candidates || []).forEach((c) => {
    if (!c.name) return;
    rows.push({
      name: c.name, you: false, classification: c.classification,
      rating: { value: null, status: "UNABLE_TO_VERIFY" },
      reviews: { value: null, status: "UNABLE_TO_VERIFY" },
      location: null, category: null, status: c.status || "AI_SUGGESTED", note: c.note,
    });
  });
  return { your, rows };
}

function Metric({ m, suffix = "", icon }) {
  if (!m || m.value == null) {
    return <span className="text-slate-400 text-xs italic" data-testid="bc-unable">Unable to verify</span>;
  }
  return (
    <span className="inline-flex items-center gap-1 font-semibold text-slate-900">
      {icon}{m.value}{suffix}
    </span>
  );
}

function Row({ r, testid }) {
  return (
    <tr data-testid={testid} className={r.you ? "bg-indigo-50/70" : "bg-white even:bg-slate-50/60"}>
      <td className="px-4 py-3 align-top">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-semibold ${r.you ? "text-indigo-900" : "text-slate-800"}`}>{r.name}</span>
          {r.you ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-indigo-300 bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-700">
              <Trophy className="w-3 h-3" /> You
            </span>
          ) : (
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${CLASS_CHIP[r.classification] || CLASS_CHIP.UNCATEGORIZED}`}>
              {CLASS_LABEL[r.classification] || "Uncategorized"}
            </span>
          )}
        </div>
        {r.category && <p className="text-[11px] text-slate-400 mt-0.5 capitalize">{String(r.category).replace(/_/g, " ")}</p>}
        {r.note && <p className="text-[11px] text-slate-500 mt-1 leading-snug max-w-[240px]">{r.note}</p>}
      </td>
      <td className="px-4 py-3 align-top text-right"><Metric m={r.rating} icon={<Star className="w-3.5 h-3.5 text-amber-500" />} /></td>
      <td className="px-4 py-3 align-top text-right"><Metric m={r.reviews} /></td>
      <td className="px-4 py-3 align-top">
        {r.location
          ? <span className="inline-flex items-start gap-1 text-xs text-slate-600"><MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />{r.location}</span>
          : <span className="text-slate-400 text-xs italic">{r.you ? "—" : "Unable to verify"}</span>}
      </td>
      <td className="px-4 py-3 align-top text-right"><ProvBadge status={r.status} /></td>
    </tr>
  );
}

export function Battlecard({ report, business, scanId }) {
  const comp = report?.competitors || {};
  const { your, rows } = buildRows(report, business);
  // Only render when there is at least one competitor to compare against.
  if (!rows.length) return null;

  const positioning = comp.positioning || {};
  const sourceNote = SOURCE_NOTE[comp.source] || "";

  return (
    <motion.div {...fade} className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8 shadow-sm" data-testid="battlecard">
      <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <Swords className="w-5 h-5 text-slate-700" />
          <h3 className="font-heading text-xl font-semibold text-slate-900">Side-by-Side Battlecard</h3>
        </div>
        <a href={`${API}/scan/${scanId}/battlecard.pdf`} data-testid="battlecard-download"
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2 transition-colors active:scale-95">
          <Download className="w-4 h-4" /> Download battlecard
        </a>
      </div>
      <p className="text-sm text-slate-500 mb-5 max-w-2xl">
        Your confirmed business compared with its competitors. We only show information supported by verified
        provider data, your own entries, or clearly-labelled AI-assisted leads — never invented statistics.
      </p>

      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="bg-slate-900 text-white text-left">
              <th className="px-4 py-3 font-semibold">Business</th>
              <th className="px-4 py-3 font-semibold text-right">Rating</th>
              <th className="px-4 py-3 font-semibold text-right">Reviews</th>
              <th className="px-4 py-3 font-semibold">Location</th>
              <th className="px-4 py-3 font-semibold text-right">Source</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="battlecard-rows">
            <Row r={your} testid="battlecard-you" />
            {rows.map((r, i) => <Row key={i} r={r} testid={`battlecard-competitor-${i}`} />)}
          </tbody>
        </table>
      </div>

      {positioning.available && positioning.insights?.length > 0 && (
        <div className="mt-6" data-testid="battlecard-positioning">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-4 h-4 text-indigo-600" />
            <h4 className="font-heading font-semibold text-slate-900">Where you stand</h4>
          </div>
          <ul className="space-y-1.5">
            {positioning.insights.slice(0, 5).map((ins, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />{ins}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6 rounded-lg bg-slate-50 border border-slate-200 p-4 flex gap-3">
        <Info className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
        <p className="text-xs text-slate-500 leading-relaxed">
          {sourceNote && <><span className="font-semibold text-slate-600">Competitor data source:</span> {sourceNote}. </>}
          Values shown as “Unable to verify” or “Not available” are not estimated or fabricated. Connect an
          authoritative provider (e.g. Google Places, in Admin) to upgrade AI-assisted competitors to a VERIFIED
          side-by-side comparison with real ratings and review counts.
        </p>
      </div>
    </motion.div>
  );
}
